import { type GenericMap, type SortInfo, type SortType, type WithId } from "@common/declarations";
import { naturalCompare } from "@common/lib/misc";
import { isArray, isEqual, isFunction, uniqBy } from "lodash";
import moment from "moment";

type ID = string | number;

export const areEqual = <T extends any>(
  a: T[],
  b: T[],
  equalFn?: (x: T) => any,
  compareFn?: (a: T, b: T) => boolean
) => {
  if (a.length !== b.length) return false;

  return a.every((x, i) => {
    const y = equalFn ? b.find((y) => equalFn(y) === equalFn(x)) : b[i];
    if (!y) return false;

    return (compareFn ?? isEqual)(x, y);
  });
};

/**
 * Does not modify original array
 */
export const updateItem = <T extends WithId<ID>>(arr: T[], mixed: ID, updateFn: (item: T) => T): T[] =>
  arr.map((item) => ((isFunction(mixed) ? mixed(item) : item.id === mixed) ? updateFn(item) : item));

/**
 * Does not modify original array
 */
export const updateItemBy = <T extends {}>(arr: T[], matchFn: (item: T) => boolean, updateFn: (item: T) => T): T[] =>
  arr.map((item) => (matchFn(item) ? updateFn(item) : item));

/**
 * Does not modify original array
 */
export const move = <T>(arr: T[], item: T, newIndex: number): T[] => {
  const oldIndex = arr.findIndex((x) => item === x);
  return moveItem(arr, { oldIndex, newIndex });
};

/**
 * Does not modify original array
 */
export const moveItem = <T>(arr: T[], { oldIndex, newIndex }: any): T[] => {
  const items = arr.slice();
  const item = items.splice(oldIndex, 1)[0];
  items.splice(newIndex, 0, item);
  return items;
};

// TODO: does not seem to work as expected
export const selectMany = <T>(arr: T[], prop: string): T[] => {
  const _select = (res: any[], item: any) => {
    res.push(item);
    if (Array.isArray(item[prop])) {
      item[prop].forEach((sitem) => _select(res, sitem));
    }
  };

  const result = [];
  arr.forEach((item) => _select(result, item));

  return result;
};

/**
 * Does not modify original array
 */
export const insert = (arr: any[], toAdd: any, index: number) => {
  const copy = arr.slice();
  copy.splice(index, 0, toAdd);
  return copy;
};

/**
 * Does not modify original array
 */
export const insertAfter = <T>(arr: T[], match: (item: T) => boolean, mixed: T | ((item: T) => T)): T[] => {
  const index = arr.findIndex(match);
  const newArray = arr.slice();
  newArray.splice(index + 1, 0, isFunction(mixed) ? mixed(arr[index]) : mixed);
  return newArray;
};

/**
 * Does not modify original array
 */
export const insertBefore = <T>(arr: T[], match: (item: T) => boolean, item: T): T[] => {
  const index = arr.findIndex(match);
  const newArray = arr.slice();
  newArray.splice(index, 0, item);
  return newArray;
};

/**
 * Does not modify original array
 */
export const push = (arr: any[], toAdd: any) => {
  const copy = arr.slice();
  copy.push(toAdd);
  return copy;
};

/**
 * Does not modify original array
 */
export const findAndRemove = <T = any>(arr: T[], matchFn: (item: T) => boolean) => {
  const index = arr.findIndex(matchFn);
  if (index === -1) return arr;

  const copy = arr.slice();
  copy.splice(index, 1);
  return copy;
};

/**
 * Does not modify original array
 */
export const removeAt = (arr: any[], index: number) => {
  const copy = arr.slice();
  copy.splice(index, 1);
  return copy;
};

/**
 * Does not modify original array
 */
export const remove = (arr: any[], toDelete: any) => {
  const index = isFunction(toDelete) ? arr.findIndex(toDelete) : arr.indexOf(toDelete);
  if (index === -1) return arr;

  return removeAt(arr, index);
};

/**
 * Does not modify original array
 */
export const removeItem = <T>(items: T[], match: (item: T) => boolean): T[] => {
  const index = items.findIndex(match);
  if (index === -1) return items;

  const newArray = items.slice();
  newArray.splice(index, 1);
  return newArray;
};

/**
 * Does not modify original array
 */
export const removeItemAt = <T>(items: T[], index: number): T[] => {
  const newArray = items.slice();
  newArray.splice(index, 1);
  return newArray;
};

export const unique = <T>(arr: T[]): T[] => Array.from(new Set<T>(arr));
export const uniqueBy = <T>(arr: T[], prop: string): T[] => {
  const map = arr.reduce((res, item) => ({ ...res, [item[prop]]: item }), {});

  return (<any>Object).values(map);
};

export const EMPTY_ARRAY = [];

export const compare = <T>(a: T, b: T, key: keyof T, type?: SortType): number => {
  const av = a[key as any];
  const bv = b[key as any];

  if (type === "natural") {
    return naturalCompare(av, bv);
  }

  if (type === "date") {
    return moment(av).valueOf() - moment(bv).valueOf();
  }

  if (typeof av === "number" && typeof bv === "number") return av - bv;

  if (typeof av === "boolean" && typeof bv === "boolean") return av === bv ? 0 : av ? 1 : -1;

  return (av ?? "").toString().localeCompare(bv ?? "");
};

export const merge = <T>(a: T[], b: T[], key: keyof T, merge: (a: T, b: T) => T = Object.assign): T[] => {
  const res = a.slice();
  for (const bi of b) {
    const ai = a.find((x) => x[key] === bi[key]);
    if (ai) {
      res.push(merge(ai, bi));
    } else {
      res.push(bi);
    }
  }
  return res;
};

type SortFn<T> = (a: T, b: T) => number;
export const sort = <T>(items: T[], sortedBy: SortInfo<T>[] | SortInfo<T> | SortFn<T> | undefined): T[] => {
  if (sortedBy) {
    if (isFunction(sortedBy)) return items.slice().sort(sortedBy);

    const info = isArray(sortedBy) ? sortedBy : [sortedBy];
    return items.slice().sort((a, b) => {
      for (let i of info) {
        const diff = compare(a, b, i.key, i.type);
        if (diff !== 0) return diff * (i.order === "desc" ? -1 : 1);
      }
      return 0;
    });
  }
  return items;
};

export const getPage = <T>(data: T[], page: number, size: number): T[] => {
  let start = (page - 1) * size;
  let end = start + size;

  return data.slice(start, end);
};

export const toMap = <T, R = any>(items: T[], getKey: (item: T) => any, getValue: (item: T) => R): GenericMap<R> =>
  items.reduce((res, x) => ({ ...res, [getKey(x)]: getValue(x) }), {});

export const isUniqBy = <T>(items: T[], key: keyof T): boolean => uniqBy(items, (x) => x[key]).length !== items.length;
